import type { HtmlAnalyzer } from './html-analyzer';
import type { ContentJson, Heading, KeywordDensity } from './interfaces';

/**
 * Constants for SEO analysis thresholds
 */
const SEO_THRESHOLDS = {
  KEYWORD: {
    MIN_DENSITY: 0.46,
    MAX_DENSITY: 1.1,
  },
  SUB_KEYWORD: {
    MIN_DENSITY: 0.12,
    MAX_DENSITY: 0.9,
    EXTREME_LOW: 0.09,
  },
  META_DESCRIPTION: {
    MAX_LENGTH: 160,
    MIN_LENGTH: 100,
    MIN_DENSITY: 2,
    MAX_DENSITY: 5,
  },
  TITLE: {
    MIN_LENGTH: 40,
    MAX_LENGTH: 70,
  },
  SUB_KEYWORD_META: {
    MIN_DENSITY: 2,
    MAX_DENSITY: 5,
  },
} as const;

export class SeoAnalyzer {
  public messages: {
    warnings: string[];
    goodPoints: string[];
    minorWarnings: string[];
  };
  public keywordDensity: number;
  private readonly headings: Heading[];

  constructor(
    private readonly content: ContentJson,
    private readonly htmlAnalyzer: HtmlAnalyzer,
    private readonly strictMode: boolean = false,
  ) {
    this.messages = {
      warnings: [],
      goodPoints: [],
      minorWarnings: [],
    };
    this.keywordDensity = this.calculateDensity(this.content.keyword);
    this.headings = this.getHeadings();
    this.assignMessages();
  }

  /**
   * Gets headings including title if not in strict mode
   */
  private getHeadings(): Heading[] {
    const headings = this.htmlAnalyzer.getAllHeadingTags();
    if (!this.strictMode && this.content.title) {
      headings.push({
        tag: 'H1',
        text: this.content.title,
      });
    }
    return headings;
  }

  /**
   * Gets density metrics for all sub-keywords
   */
  getSubKeywordsDensity(): KeywordDensity[] {
    return this.content.subKeywords.map((subKeyword) => ({
      keyword: subKeyword,
      density: this.calculateDensity(subKeyword),
    }));
  }

  /**
   * Gets keyword metrics for title analysis
   */
  public getKeywordInTitle(keyword: string | null = null): KeywordDensity {
    const targetKeyword = keyword ?? this.content.keyword;
    const density = this.calculateDensity(targetKeyword, this.content.title);
    const position = this.getPosition(this.content.title, targetKeyword);

    return {
      keyword: targetKeyword,
      density,
      position: position >= 0 ? position : 0,
    };
  }

  /**
   * Gets position of keyword in text
   */
  private getPosition(text: string | null, keyword: string | null): number {
    if (!text || !keyword) return -1;
    const cleanText = this.htmlAnalyzer.getPureText(text.toLowerCase());
    const cleanKeyword = keyword.toLowerCase();
    const index = cleanText.indexOf(cleanKeyword);
    if (index === -1) return -1;
    return cleanText.substring(0, index).split(' ').length;
  }

  public getSubKeywordsInTitle(): Array<KeywordDensity> {
    return this.content.subKeywords.map((keyword) => ({
      keyword,
      density: this.calculateDensity(keyword, this.content.title),
      position: this.getPosition(this.content.title, keyword) || 0,
    }));
  }

  getKeywordInMetaDescription(keyword: string | null = null): KeywordDensity {
    const targetKeyword = keyword ?? this.content.keyword;
    const density = this.calculateDensity(
      targetKeyword,
      this.content.metaDescription,
    );

    return {
      keyword: targetKeyword,
      density,
      position: this.getPosition(this.content.metaDescription, targetKeyword),
    };
  }

  getSubKeywordsInMetaDescription(): KeywordDensity[] {
    return this.content.subKeywords.map((keyword) =>
      this.getKeywordInMetaDescription(keyword),
    );
  }

  /**
   * Calculates overall SEO score based on warnings and good points
   */
  getSeoScore(): number {
    const { warnings, goodPoints, minorWarnings } = this.messages;
    const totalPoints =
      warnings.length +
      goodPoints.length +
      (minorWarnings.length >= 10
        ? minorWarnings.length / 10
        : minorWarnings.length >= 4
          ? minorWarnings.length / 4
          : minorWarnings.length);
    if (totalPoints === 0) return 0;

    return Math.min((goodPoints.length / totalPoints) * 100, 100);
  }

  /**
   * Calculates keyword-specific SEO score
   */
  getKeywordSeoScore(): number {
    const keywordInTitle = this.getKeywordInTitle();
    const subKeywordsInTitle = this.getSubKeywordsInTitle();
    const subKeywordsDensity = this.getSubKeywordsDensity();

    const scores = [
      keywordInTitle.density * 10,
      subKeywordsInTitle.length * 10,
      this.keywordDensity * 10,
      ...subKeywordsDensity.map(({ density }) => density * 10),
    ];

    return Math.min(
      scores.reduce((sum, score) => sum + score, 0) / scores.length,
      100,
    );
  }

  getTitleWordCount(): number {
    return this.htmlAnalyzer.getWordCount(this.content.title);
  }

  /**
   * Returns the messages object.
   * @return object The messages object.
   * @example
   * {
   *    goodPoints: [],
   *    warnings: [],
   *    minorWarnings: [],
   * }
   * @see SeoAnalyzer.messages
   */
  private assignMessages(): void {
    const analyzers = [
      this.assignMessagesForKeyword,
      this.assignMessagesForSubKeywords,
      this.assignMessagesForTitle,
      this.assignMessagesForLinks,
      this.assignMessagesForMetaDescription,
      this.assignMessagesForKeywordInHeadings,
      this.assignMessagesForSubKeywordsInHeadings,
      this.assignMessagesForHeadings,
    ];

    analyzers.forEach((analyzer) => analyzer.call(this));
  }

  /**
   * Calculates the density of a keyword in the given string of body text.
   * @param keyword Should not be null.
   * @param bodyText If null, it will use the default value, i.e. `this.htmlAnalyzer.bodyText`
   */
  calculateDensity(keyword: string, text: string | null = null): number {
    const wordCount = text
      ? this.htmlAnalyzer.getWordCount(text)
      : this.htmlAnalyzer.getWordCount();

    if (wordCount === 0) return 0;

    const occurrences = this.countOccurrencesInString(keyword, text);
    return (occurrences / wordCount) * 100;
  }

  /**
   * Counts occurrences of keyword in text
   */
  public countOccurrencesInString(
    keyword: string = this.content.keyword,
    text: string | null = null,
  ): number {
    const targetText = text
      ? text.toLowerCase()
      : this.htmlAnalyzer.getTextContent();

    // Use word boundary to match whole words only
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    const matches = targetText.match(regex);
    return matches ? matches.length : 0;
  }

  /**
   * Analyzes main keyword presence and density
   * Adds warnings for missing keywords or density issues
   */
  private assignMessagesForKeyword(): void {
    if (!this.content.keyword) {
      this.messages.warnings.push('Missing main keyword, please add one.');
      return;
    }

    this.messages.goodPoints.push(
      `Good, your content has a keyword "${this.content.keyword}".`,
    );

    if (this.keywordDensity > 5) {
      this.messages.warnings.push('Serious keyword overstuffing.');
      return;
    }

    if (this.keywordDensity < SEO_THRESHOLDS.KEYWORD.MIN_DENSITY) {
      this.messages.warnings.push(
        `Keyword density is too low (${this.keywordDensity.toFixed(2)}%).`,
      );
    } else if (this.keywordDensity > SEO_THRESHOLDS.KEYWORD.MAX_DENSITY) {
      this.messages.warnings.push(
        `Keyword density is too high (${this.keywordDensity.toFixed(2)}%).`,
      );
    } else {
      this.messages.goodPoints.push(
        `Keyword density is ${this.keywordDensity.toFixed(2)}%.`,
      );
    }
  }

  /**
   * Analyzes sub-keywords presence and density
   * Checks if sub-keywords exist and their density is within acceptable ranges
   */
  private assignMessagesForSubKeywords(): void {
    if (!this.content.subKeywords.length) {
      this.messages.minorWarnings.push('Missing sub keywords.');
      return;
    }

    this.messages.goodPoints.push(
      `Content has sub keywords: "${this.content.subKeywords.join(', ')}".`,
    );

    this.getSubKeywordsDensity().forEach(({ keyword, density }) => {
      if (density > SEO_THRESHOLDS.SUB_KEYWORD.MAX_DENSITY) {
        this.messages.warnings.push(
          `Sub keyword "${keyword}" density too high (${density.toFixed(2)}%).`,
        );
      } else if (density < SEO_THRESHOLDS.SUB_KEYWORD.MIN_DENSITY) {
        const severity =
          density < SEO_THRESHOLDS.SUB_KEYWORD.EXTREME_LOW ? 'too low' : 'low';
        this.messages.minorWarnings.push(
          `Sub keyword "${keyword}" density ${severity} (${density.toFixed(2)}%).`,
        );
      } else {
        this.messages.goodPoints.push(
          `Sub keyword "${keyword}" density good (${density.toFixed(2)}%).`,
        );
      }
    });
  }

  /**
   * Analyzes title tag for length and keyword presence
   * Checks title length and presence of main/sub keywords
   */
  private assignMessagesForTitle(): void {
    if (!this.content.title) {
      this.messages.warnings.push('Missing title tag.');
      return;
    }

    const { length } = this.content.title;
    if (length > SEO_THRESHOLDS.TITLE.MAX_LENGTH) {
      this.messages.warnings.push('Title tag is too long.');
    } else if (length < SEO_THRESHOLDS.TITLE.MIN_LENGTH) {
      this.messages.warnings.push('Title tag is too short.');
    } else {
      this.messages.goodPoints.push(
        `Title length good (${length} characters).`,
      );
    }

    const keywordInTitle = this.getKeywordInTitle();
    if (keywordInTitle.density) {
      this.messages.goodPoints.push(
        `Keyword density in title: ${keywordInTitle.density.toFixed(2)}%.`,
      );
    } else {
      this.messages.warnings.push('No main keyword in title.');
    }

    const subKeywordsInTitle = this.getSubKeywordsInTitle();
    if (subKeywordsInTitle.length) {
      this.messages.goodPoints.push(
        `Title contains ${subKeywordsInTitle.length} sub keywords.`,
      );
    } else {
      this.messages.minorWarnings.push('No sub keywords in title.');
    }
  }

  /**
   * Analyzes internal and external links
   * Checks link counts and duplicate links based on content length
   */
  private assignMessagesForLinks(): void {
    const wordCount = this.htmlAnalyzer.getWordCount();
    const internalLinks = this.htmlAnalyzer.getInternalLinks();
    const externalLinks = this.htmlAnalyzer.getOutboundLinks();

    this.analyzeInternalLinks(internalLinks.unique.length, wordCount);
    this.analyzeExternalLinks(externalLinks.unique.length, wordCount);
    this.analyzeDuplicateLinks(
      internalLinks.duplicate.length,
      externalLinks.duplicate.length,
    );
  }

  /**
   * Analyzes internal link count relative to content length
   * @param count - Number of unique internal links
   * @param wordCount - Total word count of content
   */
  private analyzeInternalLinks(count: number, wordCount: number): void {
    if (count < wordCount / 300) {
      this.messages.warnings.push(
        `Not enough internal links (${count}). Add more for better SEO.`,
      );
    } else {
      this.messages.goodPoints.push(`Good internal link count (${count}).`);
    }
  }

  /**
   * Analyzes external link count relative to content length
   * @param count - Number of unique external links
   * @param wordCount - Total word count of content
   */
  private analyzeExternalLinks(count: number, wordCount: number): void {
    if (count < wordCount / 400) {
      this.messages.warnings.push(
        `Not enough external links (${count}). Add more for better SEO.`,
      );
    }
  }

  /**
   * Analyzes duplicate links and adds appropriate messages
   * @param internalDupes - Number of duplicate internal links
   * @param externalDupes - Number of duplicate external links
   */
  private analyzeDuplicateLinks(
    internalDupes: number,
    externalDupes: number,
  ): void {
    if (internalDupes > 1) {
      this.messages.minorWarnings.push(
        `${internalDupes} duplicate internal links found.`,
      );
    } else {
      this.messages.goodPoints.push('No duplicate internal links.');
    }

    if (externalDupes > 1) {
      this.messages.minorWarnings.push(
        `${externalDupes} duplicate external links found.`,
      );
    } else {
      this.messages.goodPoints.push('No duplicate external links.');
    }
  }

  /**
   * Analyzes meta description for presence, length, and keyword usage
   */
  private assignMessagesForMetaDescription(): void {
    if (!this.content.metaDescription) {
      this.messages.warnings.push('Missing meta description.');
      return;
    }

    this.analyzeMetaDescriptionLength();
    this.analyzeMetaDescriptionKeywords();
    this.analyzeMetaDescriptionSubKeywords();
  }

  /**
   * Checks meta description length against SEO best practices
   */
  private analyzeMetaDescriptionLength(): void {
    const { length } = this.content.metaDescription;
    if (length > SEO_THRESHOLDS.META_DESCRIPTION.MAX_LENGTH) {
      this.messages.warnings.push(
        `Meta description too long (${length} characters).`,
      );
    } else if (length < SEO_THRESHOLDS.META_DESCRIPTION.MIN_LENGTH) {
      this.messages.warnings.push(
        `Meta description too short (${length} characters).`,
      );
    } else {
      this.messages.goodPoints.push(
        `Meta description length good (${length} characters).`,
      );
    }
  }

  /**
   * Analyzes keyword usage and position in meta description
   */
  private analyzeMetaDescriptionKeywords(): void {
    const keywordData = this.getKeywordInMetaDescription();

    if (keywordData.density > SEO_THRESHOLDS.META_DESCRIPTION.MAX_DENSITY) {
      this.messages.warnings.push(
        `Meta description keyword density too high (${keywordData.density.toFixed(2)}%).`,
      );
    } else if (
      keywordData.density < SEO_THRESHOLDS.META_DESCRIPTION.MIN_DENSITY
    ) {
      this.messages.warnings.push(
        `Meta description keyword density too low (${keywordData.density.toFixed(2)}%).`,
      );
    } else {
      this.messages.goodPoints.push(
        `Meta description keyword density good (${keywordData.density.toFixed(2)}%).`,
      );
    }

    if (keywordData.position > 1) {
      this.messages.minorWarnings.push(
        'Meta description should ideally start with the keyword.',
      );
    } else {
      this.messages.goodPoints.push('Meta description starts with keyword.');
    }
  }

  /**
   * Analyzes sub-keywords usage in meta description
   * Checks density and adds appropriate messages
   */
  private analyzeMetaDescriptionSubKeywords(): void {
    this.getSubKeywordsInMetaDescription().forEach(({ keyword, density }) => {
      if (density > SEO_THRESHOLDS.SUB_KEYWORD_META.MAX_DENSITY) {
        this.messages.warnings.push(
          `Sub keyword "${keyword}" density too high in meta description (${density.toFixed(2)}%).`,
        );
      } else if (density < SEO_THRESHOLDS.SUB_KEYWORD_META.MIN_DENSITY) {
        const severity = density < 0.2 ? 'too low' : 'low';
        this.messages.minorWarnings.push(
          `Sub keyword "${keyword}" density ${severity} in meta description (${density.toFixed(2)}%).`,
        );
      } else {
        this.messages.goodPoints.push(
          `Sub keyword "${keyword}" density good in meta description (${density.toFixed(2)}%).`,
        );
      }
    });
  }

  /**
   * Analyzes presence and structure of heading tags
   * Checks for missing headings and proper heading hierarchy
   */
  private assignMessagesForHeadings(): void {
    if (!this.headings.length) {
      this.messages.warnings.push('Missing headings.');
      return;
    }

    this.messages.goodPoints.push(`Found ${this.headings.length} headings.`);
    this.analyzeHeadingHierarchy();
  }

  /**
   * Analyzes heading hierarchy (h1, h2, h3)
   * Ensures proper heading structure and adds appropriate messages
   */
  private analyzeHeadingHierarchy(): void {
    const h1s = this.filterHeading('h1');
    const h2s = this.filterHeading('h2');
    const h3s = this.filterHeading('h3');

    if (!h1s.length) {
      this.messages.warnings.push('Missing H1 tag.');
    } else if (h1s.length > 1) {
      this.messages.warnings.push('Multiple H1 tags found.');
    } else {
      this.messages.goodPoints.push('H1 tag present.');
    }

    if (!h2s.length) {
      this.messages.warnings.push('Missing H2 tag.');
    } else {
      this.messages.goodPoints.push('H2 tags present.');
    }

    if (!h3s.length) {
      this.messages.minorWarnings.push('Consider adding H3 tags.');
    } else {
      this.messages.goodPoints.push('H3 tags present.');
    }
  }

  /**
   * Filters headings by tag name
   * @param tag - Heading tag to filter by (h1, h2, etc.)
   * @returns Array of headings matching the specified tag
   */
  private filterHeading(tag: string): Heading[] {
    return this.headings.filter(
      (heading) => heading.tag.toLowerCase() === tag.toLowerCase(),
    );
  }

  /**
   * Analyzes keyword usage in heading tags
   * Checks for presence of main keyword in each heading
   */
  private assignMessagesForKeywordInHeadings(): void {
    this.headings.forEach((heading) => {
      const keywordCount = this.countOccurrencesInString(
        this.content.keyword,
        heading.text,
      );

      if (keywordCount > 0) {
        this.messages.goodPoints.push(
          `Keyword found in ${heading.tag} "${heading.text}".`,
        );
      } else {
        this.messages.minorWarnings.push(
          `Consider adding keyword to ${heading.tag} "${heading.text}".`,
        );
      }
    });
  }

  /**
   * Analyzes sub-keywords usage in heading tags
   * Checks for presence of each sub-keyword in headings
   */
  private assignMessagesForSubKeywordsInHeadings(): void {
    this.headings.forEach((heading) => {
      this.content.subKeywords.forEach((subKeyword) => {
        const count = this.countOccurrencesInString(subKeyword, heading.text);

        if (count > 0) {
          this.messages.goodPoints.push(
            `Sub keyword "${subKeyword}" found in ${heading.tag}.`,
          );
        } else {
          this.messages.minorWarnings.push(
            `Consider adding sub keyword "${subKeyword}" to ${heading.tag}.`,
          );
        }
      });
    });
  }
}
